// ═══════════════════════════════════════════════════════════
// Auth Store — staff authentication
//
// Tokens live in HTTP-only cookies managed server-side.
// This store holds only the user identity (no tokens).
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { gabLogin, gabLogout, gabCheckSession } from '../api/client';
import type { GABUser } from '../api/types';

export type StaffRole = 'admin' | 'coordinator' | 'department_staff' | 'supervisor' | 'legal';

export interface StaffUser extends GABUser {
  staffRole?: StaffRole;
  departmentId?: string;
  jurisdictionId?: string;
}

interface AuthState {
  user: StaffUser | null;
  loading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: StaffUser) => void;
}

const USER_KEY = 'foia_user';

function loadCachedUser(): StaffUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) as StaffUser : null;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => {
  const cached = loadCachedUser();

  // Verify the session cookie is still valid asynchronously.
  // If the cookie has expired (or was cleared), we clear the cached user.
  if (cached) {
    gabCheckSession().then(ok => {
      if (!ok) {
        localStorage.removeItem(USER_KEY);
        set({ user: null });
      }
    }).catch(() => {
      localStorage.removeItem(USER_KEY);
      set({ user: null });
    });
  }

  return {
    user: cached,
    loading: false,
    error: null,

    login: async (email, password) => {
      set({ loading: true, error: null });
      try {
        const { user } = await gabLogin(email, password);
        const staffUser: StaffUser = { ...user };
        localStorage.setItem(USER_KEY, JSON.stringify(staffUser));
        set({ user: staffUser, loading: false });
      } catch (e) {
        set({ error: e instanceof Error ? e.message : 'Login failed', loading: false });
        throw e;
      }
    },

    logout: () => {
      gabLogout(); // fire-and-forget — clears the HTTP-only cookie server-side
      localStorage.removeItem(USER_KEY);
      set({ user: null });
    },

    setUser: (user) => {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user });
    },
  };
});
